import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cpGpMatchClient } from '../../../infrastructure/CpGpMatchClient';
import { verifiedImagesClient } from '../../../infrastructure/VerifiedImagesClient';
import { skinSeoulProductsClient } from '../../../infrastructure/SkinSeoulProductsClient';
import { useCMWorkflowByGPI } from '../sourcing/useCMWorkflowDetail';
import type { CpGpMatchedProduct } from '../../../models/Coupang';

interface UseMatchedProductDataParams {
  cpProductId?: string | undefined;
  cpItemId?: string | undefined;
  productDataFromState?: CpGpMatchedProduct | undefined;
}

export const useMatchedProductData = ({ cpProductId, cpItemId, productDataFromState }: UseMatchedProductDataParams) => {
  const {
    data: matchedProduct,
    isLoading: matchLoading,
    error: matchError,
  } = useQuery({
    queryKey: ['cpGpMatch', cpProductId, cpItemId],
    queryFn: async () => {
      if (productDataFromState) {
        return productDataFromState;
      }

      if (!cpProductId || !cpItemId) {
        throw new Error('Invalid product URL. Missing product identifiers.');
      }

      const response = await cpGpMatchClient.getMatchByCpProductAndItemId(cpProductId, cpItemId);

      if (!response.success || !response.match) {
        throw new Error(response.message || 'Product not found in the matched products database.');
      }

      return response.match;
    },
    enabled: Boolean(cpProductId && cpItemId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: cmWorkflowData } = useCMWorkflowByGPI(
    matchedProduct?.gpProductId || '',
    matchedProduct?.cpProductId,
    Boolean(matchedProduct?.gpProductId),
  );

  const skusFromWorkflow = useMemo(() => {
    if (!cmWorkflowData || cmWorkflowData.length === 0) {
      return [];
    }
    const skuCpiiMappings = cmWorkflowData[0]?.skuCpiiMappings || [];
    return skuCpiiMappings.map((m: any) => m.sku).filter((sku: string) => Boolean(sku));
  }, [cmWorkflowData]);

  const { data: fetchedProducts, refetch: refetchProductsBySku } = useQuery({
    queryKey: ['productsBySku', skusFromWorkflow.join(',')],
    queryFn: async () => {
      if (skusFromWorkflow.length === 0) {
        return [];
      }
      return await skinSeoulProductsClient.getProductsBySku(skusFromWorkflow);
    },
    enabled: skusFromWorkflow.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: verifiedImages, refetch: refetchVerifiedImages } = useQuery({
    queryKey: ['verifiedImages', matchedProduct?.gpProductId, matchedProduct?.cpProductId, matchedProduct?.cpItemId],
    queryFn: async () => {
      if (!matchedProduct) {
        return null;
      }
      const response = await verifiedImagesClient.getVerifiedImages({
        gpProductId: matchedProduct.gpProductId,
        cpProductId: matchedProduct.cpProductId,
        cpItemId: matchedProduct.cpItemId,
      });
      return response.verifiedImageUrls || [];
    },
    enabled: Boolean(matchedProduct),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  return {
    matchedProduct,
    matchLoading,
    matchError,
    cmWorkflowData,
    skusFromWorkflow,
    productsBySku: fetchedProducts || [],
    verifiedImages: verifiedImages || [],
    refetchVerifiedImages,
    refetchProductsBySku,
  };
};
